import { useCallback, useEffect, useRef, useState } from "react";

/* Canvas signature pad — the web counterpart of the ops-app signing canvas
   (leoparda sign-user-bike-document: canvas → PNG → ms-files). Smooth
   quadratic strokes, HiDPI-aware, touch + mouse via pointer events, and a
   PNG data-URL export compatible with the {{signature}} <img> slot the
   contract template embeds. */

export interface SignaturePadHandle {
  toDataUrl: () => string | null;
  clear: () => void;
}

export function SignaturePad({
  onInkChange,
  registerHandle,
}: {
  onInkChange: (inked: boolean) => void;
  registerHandle: (handle: SignaturePadHandle) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [inked, setInked] = useState(false);

  const setup = useCallback((canvas: HTMLCanvasElement) => {
    const ratio = Math.max(1, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext("2d")!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#eef1f5";
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setup(canvas);

    const point = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const down = (e: PointerEvent) => {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      drawing.current = true;
      last.current = point(e);
    };
    const move = (e: PointerEvent) => {
      if (!drawing.current || !last.current) return;
      const ctx = canvas.getContext("2d")!;
      const next = point(e);
      const mid = { x: (last.current.x + next.x) / 2, y: (last.current.y + next.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.quadraticCurveTo(last.current.x, last.current.y, mid.x, mid.y);
      ctx.lineTo(next.x, next.y);
      ctx.stroke();
      last.current = next;
      setInked(true);
    };
    const up = () => {
      drawing.current = false;
      last.current = null;
    };

    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", up);
    canvas.addEventListener("pointercancel", up);
    return () => {
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", up);
      canvas.removeEventListener("pointercancel", up);
    };
  }, [setup]);

  useEffect(() => onInkChange(inked), [inked, onInkChange]);

  useEffect(() => {
    registerHandle({
      toDataUrl: () => {
        const canvas = canvasRef.current;
        if (!canvas || !inked) return null;
        // Re-plot on a white background: the PDF template shows the signature
        // over paper, and the pad draws light-on-dark.
        const out = document.createElement("canvas");
        out.width = canvas.width;
        out.height = canvas.height;
        const ctx = out.getContext("2d")!;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.filter = "invert(1)";
        ctx.drawImage(canvas, 0, 0);
        return out.toDataURL("image/png");
      },
      clear: () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d")!;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        setInked(false);
      },
    });
  }, [inked, registerHandle]);

  return (
    <div className={"lpc-signpad" + (inked ? " is-inked" : "")}>
      <canvas ref={canvasRef} data-testid="signature-canvas" />
      {!inked && <div className="lpc-signpad__hint">Assine aqui com o dedo ou o mouse</div>}
      <div className="lpc-signpad__line" />
    </div>
  );
}
